// Device detection (mobile mode foundation).
//
// Gathers every signal the browser exposes about the current device and derives
// a single `mode` switch that the views use to branch between the desktop layout
// and the mobile layout. The robust, event-driven signals (width, height,
// orientation, the mobile hint) gate the mode; OS and device model are captured
// best-effort for display/telemetry only — browsers expose them unreliably
// (Safari never reports a model; a desktop's make/model is never exposed at all),
// so we never gate layout on them.
//
// The single source of truth for "is this a touch device" also lives here
// (`matchMedia("(pointer: coarse)")`); `components/Tooltip.tsx` imports it.

import { useEffect, useState } from "react";

/** Shared with the CSS `@media (max-width: 768px)` breakpoint. */
export const MOBILE_BREAKPOINT = 768;

export type DeviceMode = "mobile" | "desktop";
export type Orientation = "portrait" | "landscape";

export interface DeviceInfo {
  width: number;
  height: number;
  orientation: Orientation;
  /** `pointer: coarse` — a touch screen. */
  isTouch: boolean;
  /** `navigator.userAgentData.mobile`, or a UA-string fallback. */
  mobileHint: boolean;
  /** "iOS" | "Android" | "Windows" | "macOS" | "Linux" | null. */
  os: string | null;
  /** High-entropy model when the browser offers it (Chromium phones), else null. */
  deviceModel: string | null;
  /** The derived layout switch. The only field that gates rendering. */
  mode: DeviceMode;
}

// `navigator.userAgentData` is not in the DOM lib types yet.
interface UADataValues {
  platform?: string;
  mobile?: boolean;
  model?: string;
}
interface UADataLike extends UADataValues {
  getHighEntropyValues?: (hints: string[]) => Promise<UADataValues>;
}
function uaData(): UADataLike | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as unknown as { userAgentData?: UADataLike }).userAgentData;
}

function detectOs(): string | null {
  const ua = uaData();
  const platform = ua?.platform;
  if (platform) {
    // userAgentData.platform → normalise to our buckets.
    if (/android/i.test(platform)) return "Android";
    if (/ios|iphone|ipad/i.test(platform)) return "iOS";
    if (/mac/i.test(platform)) return "macOS";
    if (/win/i.test(platform)) return "Windows";
    if (/linux/i.test(platform)) return "Linux";
    return platform;
  }
  // Fallback: parse the UA string (Safari, Firefox).
  if (typeof navigator === "undefined") return null;
  const s = navigator.userAgent;
  if (/android/i.test(s)) return "Android";
  if (/iphone|ipad|ipod/i.test(s)) return "iOS";
  if (/macintosh|mac os x/i.test(s)) return "macOS";
  if (/windows/i.test(s)) return "Windows";
  if (/linux/i.test(s)) return "Linux";
  return null;
}

function detectMobileHint(): boolean {
  const ua = uaData();
  if (typeof ua?.mobile === "boolean") return ua.mobile;
  if (typeof navigator === "undefined") return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

function readOrientation(): Orientation {
  if (typeof window === "undefined") return "landscape";
  if (window.matchMedia) {
    return window.matchMedia("(orientation: portrait)").matches
      ? "portrait"
      : "landscape";
  }
  return window.innerHeight >= window.innerWidth ? "portrait" : "landscape";
}

function readIsTouch(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Derive the layout switch from the robust signals only. Mobile when the
 * viewport is narrow AND it's either portrait or a known mobile device — so a
 * phone rotated to landscape, or a narrow desktop window, stays desktop.
 */
function deriveMode(
  width: number,
  orientation: Orientation,
  mobileHint: boolean,
): DeviceMode {
  const narrow = width <= MOBILE_BREAKPOINT;
  if (narrow && (orientation === "portrait" || mobileHint)) return "mobile";
  return "desktop";
}

function snapshot(deviceModel: string | null): DeviceInfo {
  const width = typeof window === "undefined" ? 1024 : window.innerWidth;
  const height = typeof window === "undefined" ? 768 : window.innerHeight;
  const orientation = readOrientation();
  const mobileHint = detectMobileHint();
  return {
    width,
    height,
    orientation,
    isTouch: readIsTouch(),
    mobileHint,
    os: detectOs(),
    deviceModel,
    mode: deriveMode(width, orientation, mobileHint),
  };
}

/**
 * Live device snapshot, kept current via resize / orientation / pointer
 * listeners. Resize is coalesced with requestAnimationFrame so we re-render at
 * most once per frame.
 */
export function useDeviceInfo(): DeviceInfo {
  // Device model is async (high-entropy) and never changes for a session, so we
  // hold it separately and fold it into each snapshot.
  const [model, setModel] = useState<string | null>(null);
  const [info, setInfo] = useState<DeviceInfo>(() => snapshot(null));

  // One-shot best-effort high-entropy model lookup (Chromium phones only).
  useEffect(() => {
    let cancelled = false;
    const ua = uaData();
    if (ua?.getHighEntropyValues) {
      ua.getHighEntropyValues(["model"])
        .then((v) => {
          if (!cancelled && v.model) setModel(v.model);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setInfo(snapshot(model)));
    };
    update(); // re-run once model resolves / on mount
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    const mqPortrait = window.matchMedia?.("(orientation: portrait)");
    const mqTouch = window.matchMedia?.("(pointer: coarse)");
    mqPortrait?.addEventListener?.("change", update);
    mqTouch?.addEventListener?.("change", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      mqPortrait?.removeEventListener?.("change", update);
      mqTouch?.removeEventListener?.("change", update);
    };
  }, [model]);

  return info;
}

/** Standalone touch check for callers that only need the pointer signal. */
export function useIsTouchDevice(): boolean {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse)");
    const onChange = () => setTouch(mq.matches);
    onChange();
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  return touch;
}
